import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';

import {
  AddAnswerButton,
  AnswerOptionInput,
  AnswerOptionsContainer,
  AnswerOptionWrapper,
  DeleteButton,
  HintContainer,
  QuestionContainer,
  QuestionHeader,
  QuestionInputWrapper,
  SavedStatus,
  SelectContainer,
} from './styles';
import ActionButton from '../../../../../components/ActionButton/ActionButton';
import SaveIcon from '../../../../../../assets/icons/SaveIcon';
import EditIcon from '../../../../../../assets/icons/EditIcon';
import { AnswerTypeEnum, QuestionSection, QuestionTypeEnum } from '../../types';
import CustomAutoExpandingTextarea from '../../../../../components/Inputs/AutoExpandingTextarea/AutoExpandingTextarea';
import { useI18n } from '../../../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../../../i18n/locales/defaultResources';

interface QuestionFormProps {
  section: QuestionSection;
  index: number;
  loading?: boolean;
  onDelete: () => void;
  updateQuestionSection: (
    questionId: string,
    updates: Partial<QuestionSection>
  ) => void;
  handleSaveQuestion: (questionId: string) => Promise<void>;
  isQuestionValid: (section: QuestionSection) => boolean;
  onToggleEdit: () => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  section,
  index,
  loading = false,
  onDelete,
  updateQuestionSection,
  handleSaveQuestion,
  isQuestionValid,
  onToggleEdit,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useI18n();

  const hasChanges = useMemo(() => {
    if (!section.status.isEditable || !section.status.originalData)
      return false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { status, ...currentData } = section;
    const originalData = section.status.originalData;
    return JSON.stringify(currentData) !== JSON.stringify(originalData);
  }, [section]);

  const handleTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      updateQuestionSection(section.id, { text: e.target.value });
    },
    [section.id, updateQuestionSection]
  );

  // const handlePrefilledAnswerChange = useCallback(
  //   (e: ChangeEvent<HTMLTextAreaElement>) => {
  //     updateQuestionSection(section.id, { prefilledAnswer: e.target.value });
  //   },
  //   [section.id, updateQuestionSection]
  // );

  const handleHintTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      updateQuestionSection(section.id, {
        hint: { ...section.hint, text: e.target.value },
      });
    },
    [section.hint, section.id, updateQuestionSection]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      updateQuestionSection(section.id, { description: e.target.value });
    },
    [section.id, updateQuestionSection]
  );

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete]);

  // @ts-expect-error types
  return (
    <QuestionContainer>
      <QuestionHeader>
        <h3>
          {t(TranslationKeys.survey_question_form_question_title)} {index + 1}
        </h3>
        <div className="actions">
          {section.status.isSaved ? (
            section.status.isEditable || hasChanges ? (
              <>
                <ActionButton
                  variant="accept-blue"
                  buttonText={
                    loading
                      ? t(TranslationKeys.survey_question_form_updating)
                      : t(TranslationKeys.survey_question_form_update)
                  }
                  onClickAction={() => handleSaveQuestion(section.id)}
                  disabled={!hasChanges || !isQuestionValid(section) || loading}
                  icon={<SaveIcon />}
                />
                <ActionButton
                  variant="light-abort-1"
                  buttonText={
                    isDeleting
                      ? t(TranslationKeys.survey_question_form_deleting)
                      : t(TranslationKeys.survey_question_form_delete)
                  }
                  onClickAction={handleDelete}
                  disabled={!section.status.isEditable || isDeleting}
                />
              </>
            ) : (
              <SavedStatus>
                <span className="saved-text">
                  {t(TranslationKeys.survey_question_form_saved)}
                </span>
                <div
                  className="actions"
                  style={{ display: 'flex', gap: '12px' }}
                >
                  <ActionButton
                    variant="accept-blue"
                    buttonText={t(TranslationKeys.survey_question_form_edit)}
                    onClickAction={onToggleEdit}
                    icon={<EditIcon />}
                    disabled={loading}
                  />
                  <ActionButton
                    variant="light-abort-1"
                    buttonText={t(TranslationKeys.survey_question_form_delete)}
                    onClickAction={handleDelete}
                    disabled={!section.status.isEditable || isDeleting}
                  />
                </div>
              </SavedStatus>
            )
          ) : (
            <>
              <ActionButton
                variant="accept-blue"
                buttonText={
                  loading
                    ? t(TranslationKeys.survey_question_form_saving)
                    : t(TranslationKeys.survey_question_form_save_question)
                }
                onClickAction={() => handleSaveQuestion(section.id)}
                disabled={!isQuestionValid(section) || loading}
                icon={<SaveIcon />}
              />
              <ActionButton
                variant="light-abort-1"
                buttonText={t(TranslationKeys.survey_question_form_delete)}
                onClickAction={handleDelete}
                disabled={!section.status.isEditable || isDeleting}
              />
            </>
          )}
        </div>
      </QuestionHeader>

      <QuestionInputWrapper
        disabled={section.status.isSaved && !section.status.isEditable}
      >
        <SelectContainer>
          <FormControl fullWidth>
            <InputLabel>
              {t(TranslationKeys.survey_question_form_question_type)}
            </InputLabel>
            <Select
              value={section.type}
              onChange={(e) =>
                updateQuestionSection(section.id, {
                  type: e.target.value as QuestionTypeEnum,
                  answerType: undefined,
                  possibleAnswers: undefined,
                  prefilledAnswer: undefined,
                  description: '',
                })
              }
              label={t(TranslationKeys.survey_question_form_question_type)}
              disabled={!section.status.isEditable}
            >
              <MenuItem value={QuestionTypeEnum.STANDARD}>
                {t(TranslationKeys.survey_question_form_question_type_standard)}
              </MenuItem>
              <MenuItem value={QuestionTypeEnum.PREFILLED}>
                {t(
                  TranslationKeys.survey_question_form_question_type_prefilled
                )}
              </MenuItem>
            </Select>
          </FormControl>

          {section.type === QuestionTypeEnum.STANDARD && (
            <FormControl fullWidth>
              <InputLabel>
                {t(TranslationKeys.survey_question_form_answer_type)}
              </InputLabel>
              <Select
                value={section.answerType}
                onChange={(e) =>
                  updateQuestionSection(section.id, {
                    answerType: e.target.value as AnswerTypeEnum,
                    possibleAnswers:
                      e.target.value === AnswerTypeEnum.YES_NO
                        ? ['']
                        : undefined,
                  })
                }
                label={t(TranslationKeys.survey_question_form_answer_type)}
                disabled={!section.status.isEditable}
              >
                <MenuItem value={AnswerTypeEnum.YES_NO}>
                  {t(
                    TranslationKeys.survey_question_form_answer_type_single_choice
                  )}
                </MenuItem>
                {/*currently not needed for redcert, restore for Survey in the future*/}
                {/*<MenuItem value={AnswerTypeEnum.TEXTAREA}>*/}
                {/*  {t(TranslationKeys.survey_question_form_answer_type_textarea)}*/}
                {/*</MenuItem>*/}
              </Select>
            </FormControl>
          )}
        </SelectContainer>

        {section.type && (
          <>
            <CustomAutoExpandingTextarea
              value={section.text}
              onChange={handleTextChange}
              label={
                t(
                  TranslationKeys.survey_question_form_question_header
                ) as string
              }
              placeholder={
                t(
                  TranslationKeys.survey_question_form_question_header_placeholder
                ) as string
              }
              disabled={!section.status.isEditable}
            />

            <CustomAutoExpandingTextarea
              value={section.description || ''}
              onChange={handleDescriptionChange}
              placeholder={
                t(
                  TranslationKeys.survey_question_form_question_placeholder
                ) as string
              }
              disabled={!section.status.isEditable}
              label={
                t(TranslationKeys.survey_question_form_question_title) as string
              }
            />
          </>
        )}

        {/*{section.type === QuestionTypeEnum.PREFILLED && (*/}
        {/*  <CustomAutoExpandingTextarea*/}
        {/*    value={section.prefilledAnswer || ''}*/}
        {/*    onChange={handlePrefilledAnswerChange}*/}
        {/*    placeholder={*/}
        {/*      t(*/}
        {/*        TranslationKeys.survey_question_form_prefilled_answer_placeholder*/}
        {/*      ) as string*/}
        {/*    }*/}
        {/*    disabled={!section.status.isEditable}*/}
        {/*  />*/}
        {/*)}*/}

        {section.type === QuestionTypeEnum.STANDARD &&
          section.answerType === AnswerTypeEnum.YES_NO && (
            <AnswerOptionsContainer>
              {section.possibleAnswers?.map((answer, idx) => (
                <AnswerOptionWrapper key={idx}>
                  <AnswerOptionInput
                    value={answer}
                    onChange={(e) =>
                      updateQuestionSection(section.id, {
                        possibleAnswers: (section.possibleAnswers || []).map(
                          (a, i) => (i === idx ? e.target.value : a)
                        ),
                      })
                    }
                    placeholder={
                      idx === 0
                        ? (t(
                            TranslationKeys.survey_question_form_positive_answer
                          ) as string)
                        : (t(
                            TranslationKeys.survey_question_form_negative_answer
                          ) as string)
                    }
                    $isPositive={idx === 0}
                    disabled={!section.status.isEditable}
                  />
                  {section.status.isEditable && (
                    <DeleteButton
                      onClick={() =>
                        updateQuestionSection(section.id, {
                          possibleAnswers: (
                            section.possibleAnswers || []
                          ).filter((_, i) => i !== idx),
                        })
                      }
                      aria-label={t(
                        TranslationKeys.survey_question_form_remove_answer_option
                      )}
                    >
                      <ClearIcon />
                    </DeleteButton>
                  )}
                </AnswerOptionWrapper>
              ))}
              {section.status.isEditable && (
                <AddAnswerButton
                  onClick={() =>
                    updateQuestionSection(section.id, {
                      possibleAnswers: [...(section.possibleAnswers || []), ''],
                    })
                  }
                  disabled={(section.possibleAnswers?.length || 0) >= 2}
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <AddIcon />{' '}
                  {t(TranslationKeys.survey_question_form_add_answer_option)}
                  {(section.possibleAnswers?.length || 0) >= 2 && (
                    <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                      ({t(TranslationKeys.survey_question_form_max_options)})
                    </span>
                  )}
                </AddAnswerButton>
              )}
            </AnswerOptionsContainer>
          )}

        {section.type && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={section.hint.enabled}
                  onChange={(e) =>
                    updateQuestionSection(section.id, {
                      hint: {
                        ...section.hint,
                        enabled: e.target.checked,
                        text: e.target.checked ? section.hint.text || '' : '',
                      },
                    })
                  }
                  color="primary"
                  disabled={!section.status.isEditable}
                />
              }
              label={t(TranslationKeys.survey_question_form_add_hint_label)}
            />

            {section.hint.enabled && (
              <HintContainer>
                <CustomAutoExpandingTextarea
                  value={section.hint.text}
                  onChange={handleHintTextChange}
                  placeholder={
                    t(
                      TranslationKeys.survey_question_form_hint_placeholder
                    ) as string
                  }
                  disabled={!section.status.isEditable}
                />
              </HintContainer>
            )}
          </>
        )}
      </QuestionInputWrapper>
    </QuestionContainer>
  );
};

export default QuestionForm;
